/**
 * <hh-carousel> — a native horizontal scroller with ‹ › buttons.
 *
 * Phones swipe the list (CSS scroll snap does the work). On larger screens
 * the buttons move the list by one card; each button is disabled at its end.
 *
 * Markup:
 *   <hh-carousel>
 *     <button data-hh-prev>‹</button> <button data-hh-next>›</button>
 *     <ul class="hh-scroller" data-hh-track>…</ul>
 *   </hh-carousel>
 */
class HHCarousel extends HTMLElement {
  #abortController = new AbortController();
  #frame = 0;

  connectedCallback() {
    const { signal } = this.#abortController;
    const track = this.#track;
    if (!track) return;

    this.querySelector('[data-hh-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-hh-next]')?.addEventListener('click', () => this.#step(1), { signal });
    track.addEventListener('scroll', this.#scheduleUpdate, { signal, passive: true });
    window.addEventListener('resize', this.#scheduleUpdate, { signal, passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('[data-hh-track]');
  }

  /** @param {number} direction */
  #step(direction) {
    const track = this.#track;
    if (!track) return;

    const card = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = card instanceof HTMLElement ? card.offsetWidth + gap : track.clientWidth * 0.8;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;

    track.scrollBy({ left: distance * direction * rtl, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const track = this.#track;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const prev = this.querySelector('[data-hh-prev]');
    const next = this.querySelector('[data-hh-next]');

    if (prev instanceof HTMLButtonElement) prev.disabled = position <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = position >= max - 2;
    this.toggleAttribute('data-scrollable', max > 2);
  }
}

if (!customElements.get('hh-carousel')) {
  customElements.define('hh-carousel', HHCarousel);
}
